import type { Option } from "@tento-lona/sheets";
import type { RowViewport } from "@tento-lona/sheets/viewport";
import type { RenderPass } from "@tento-lona/sheets/platform";
import { DateTime, NaiveDate, Utc } from "@tento-chrono";

import { ColumnIndex } from "@tento-ui/components/sheet/column-index";
import { SheetColumn } from "./sheet-column.ts";
import { SheetLayoutWidth } from "@tento-ui/components/sheet/sheet-layout-width";
import { SheetViewModel } from "@tento-lona";
import { LiveData } from "@tento-core/livedata";

/**
 * Binds one column index's header, body and footer columns when the sheet
 * scrolls it into view.
 */
export type SheetBinder<
  Custom extends SheetIndexRenderer.ColumnInfo = SheetIndexRenderer.ColumnInfo,
> = {
  onBind: (
    sheet: SheetViewModel.Surface<SheetBinder>,
    index: ColumnIndex.Key.Resolved<Custom>,
    item: {
      $header: SheetColumn;
      $body: SheetColumn;
      $footer: Option<SheetColumn>;
    },
    widthPx: number,
    pass: SheetBinder.Pass,
    currentTime: LiveData.Readable<DateTime<Utc>>,
  ) => void;
  rebind: (
    sheet: SheetViewModel.Surface<SheetBinder>,
    column: ColumnIndex.Key.Resolved<Custom>,
    item: {
      $header: SheetColumn;
      $body: SheetColumn;
      $footer: Option<SheetColumn>;
    },
    widthPx: number,
    rebindInfo: number,
    currentTime: LiveData.Readable<DateTime<Utc>>,
  ) => void;
  /** Update static children for spans that exceed the visible viewport */
  updateStaticChildSpans?: (
    sheet: SheetViewModel.Surface<SheetBinder>,
    visibleColumns: ColumnIndex.Key.Resolved.Default.Range,
  ) => void;
  /** Clean up all static children for spans */
  cleanupStaticChildSpans?: () => void;
  /** Clear all row render modes, forcing recomputation on next bind */
  clearRowModes?: () => void;
  /**
   * Drop everything this binder borrowed from the sheet it was painting —
   * today, its `RenderSurfaceLease`. Called by the host `Sheet` when
   * the element disconnects, which is the only moment it lets go of its binder;
   * a binder torn down while still holding a lease leaves the surface (and every
   * row record under it) registered on a registry that may outlive it in the
   * sheet cache, and no witness can see that — the leak tripwire only fires on a
   * re-acquire, which a dead binder never performs.
   *
   * MUST be idempotent: disconnect can run more than once over an element's
   * life (a reparent, then a pool recycle), and the binder is re-armed lazily by
   * the next bind rather than by `connectedCallback`.
   *
   * Optional for the same reason the three hooks above are: `SheetBinder` is a
   * structural type the app layer implements too (`FlightSheetBinder`), and a
   * binder that borrows nothing owes nothing.
   */
  dispose?: () => void;
  /** Retire retained paint scenes when horizontal pool membership changes. */
  retireColumn?: (column: ColumnIndex.Key.Resolved.Default) => void;
  retainColumns?: (columns: Iterable<ColumnIndex.Key.Resolved.Default>) => void;
};

/** Types that go with `SheetBinder`: `Pass`. */
export namespace SheetBinder {
  /**
   * What one `onBind` pass is for: `update` (`RenderPass`) is `"full"` (bind
   * the column's content) or `"resize-only"` (only its width changed: re-lay
   * the cells it already shows), and `visibility` whether the column is in the viewport
   * (`"onscreen"`) or bound ahead of it in the overscan (`"offscreen"`).
   */
  export type Pass = {
    readonly update: RenderPass;
    readonly visibility: "onscreen" | "offscreen";
    /** Body-local row residency: `{ mode: "all" }` renders every row, `"window"` only the selected rows. */
    readonly rowViewport: RowViewport.Input;
  };

  /**
   * Why a full pass runs, when its caller knows (F10). `"membership"`: only
   * the row viewport's membership changed since this column's last bind (no
   * data, sheet, layout or width change was requested for it), so a binder
   * may keep a resident cell that still shows its row and column. Anything
   * else, or a pass without a reason, is `"content"`: rebind every cell.
   */
  export type PassReason = "content" | "membership";

  /** A pass that says why it runs (the sheet element sends these). */
  export type ReasonedPass = Pass & { readonly reason: PassReason };
}

/** Maps a sheet's columns to dates and binds the columns it shows. */
export interface SheetIndexRenderer<
  Info extends SheetIndexRenderer.ColumnInfo = SheetIndexRenderer.ColumnInfo,
> {
  readonly mapper: ColumnIndex.Mapper<Info>;
  readonly initialWidth: SheetLayoutWidth;
  readonly initialConstraints: SheetLayoutWidth.Constraints;

  toWidth(key: ColumnIndex.Key.Resolved<Info>): Option<number>;
  /**
   * Cheap per-index width lookup that avoids constructing a fully-
   * resolved Key. Hot path for the scroll engine's `extendOffsetRange`
   * loop, which queries widths for every newly-revealed index.
   * Renderers that don't differentiate per-index width should return
   * null and let the engine fall back to its default width.
   */
  widthForIndex(index: number): Option<number>;
  toBinder(key: ColumnIndex.Key.Resolved<Info>): Option<SheetBinder<Info>>;
}

/** Types and values that go with `SheetIndexRenderer`: `ColumnInfo`, `Default`. */
export namespace SheetIndexRenderer {
  /**
   * The least a column's index info carries: the date the column starts on
   * (`null` for a column with no date). A renderer's own info type extends
   * it (a flight column's info adds the flight segment).
   */
  export type ColumnInfo = {
    startDate: Option<NaiveDate>;
  };

  export class Default<
    Info extends ColumnInfo = ColumnInfo,
  > implements SheetIndexRenderer<Info>
  {
    initialWidth: SheetLayoutWidth = SheetLayoutWidth.fromCount(2);
    initialConstraints: SheetLayoutWidth.Constraints = {
      weak: true,
      preferredWidthPx: 240,
      minWidthPx: 60,
    };

    constructor(readonly mapper: ColumnIndex.Mapper<Info>) {}

    toWidth(_key: ColumnIndex.Key.Resolved<Info>): Option<number> {
      return null;
    }

    widthForIndex(_index: number): Option<number> {
      return null;
    }

    toBinder(_key: ColumnIndex.Key.Resolved<Info>): Option<SheetBinder> {
      return null;
    }
  }
}
